import { RHO0 } from './constants';

/**
 * International Standard Atmosphere (troposphere, up to 11 km) plus a simple
 * steady wind field. Good enough for instrument training altitudes.
 */

const T0 = 288.15; // K
const P0 = 101325; // Pa
const L = 0.0065; // K/m temperature lapse rate
const R = 287.05; // J/(kg*K)
const G = 9.80665;

export interface AtmoSample {
  density: number; // kg/m^3
  pressure: number; // Pa
  temperature: number; // K
  soundSpeed: number; // m/s
}

export function sampleISA(altitudeMeters: number): AtmoSample {
  const h = Math.max(0, altitudeMeters);
  const T = T0 - L * h;
  const P = P0 * Math.pow(T / T0, G / (L * R));
  const density = P / (R * T);
  const soundSpeed = Math.sqrt(1.4 * R * T);
  return { density, pressure: P, temperature: T, soundSpeed };
}

export function densityRatio(altitudeMeters: number): number {
  return sampleISA(altitudeMeters).density / RHO0;
}

/** Steady wind, expressed in the world NED frame (m/s). */
export interface Wind {
  north: number;
  east: number;
  down: number;
}

export const CALM_WIND: Wind = { north: 0, east: 0, down: 0 };

/**
 * Wind from meteorological convention: direction is where the wind comes FROM
 * (degrees true) and speed in knots. Returns a world NED vector (m/s).
 */
export function windFromMet(directionDegFrom: number, speedKts: number): Wind {
  const speed = speedKts * 0.514444;
  const rad = (directionDegFrom * Math.PI) / 180;
  // Wind blows toward (dir + 180). Component moving north = -speed*cos(dir).
  return {
    north: -speed * Math.cos(rad),
    east: -speed * Math.sin(rad),
    down: 0,
  };
}
